import { useEffect, useState } from "react";
import { AlertTriangle, Inbox, Loader2 } from "lucide-react";
import { formatRupiah } from "../lib/format";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Button } from "../components/ui/button";

export function TypeBadge({ type }) {
  if (type === "income")
    return (
      <span className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded-full px-2.5 py-0.5 text-xs font-semibold inline-flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500/150" /> PEMASUKAN
      </span>
    );
  return (
    <span className="bg-rose-500/15 text-rose-400 border border-rose-500/30 rounded-full px-2.5 py-0.5 text-xs font-semibold inline-flex items-center gap-1">
      <span className="w-1.5 h-1.5 rounded-full bg-rose-500/150" /> PENGELUARAN
    </span>
  );
}

export function CountUp({ value, format = formatRupiah, duration = 900 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const to = Number(value) || 0;
    let raf;
    const start = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration);
      setDisplay(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <>{format(display)}</>;
}

export function StatCard({ title, value, icon: Icon, accent, sub, testid, numeric, numFormat = formatRupiah }) {
  const accents = {
    blue: "bg-blue-500/15 text-cyan-400 border-blue-500/30",
    emerald: "bg-emerald-500/15 text-emerald-400 border-emerald-100",
    rose: "bg-rose-500/15 text-rose-400 border-rose-100",
    slate: "bg-slate-800 text-slate-400 border-slate-800",
  };
  return (
    <div
      data-testid={testid}
      className="bg-[#1E293B] border border-slate-800 rounded-2xl shadow-xl hover:shadow-cyan-500/10 hover:border-cyan-500/30 transition-all duration-300 hover:-translate-y-1 p-5 flex items-start justify-between fade-up"
    >
      <div className="min-w-0">
        <p className="text-xs font-semibold tracking-wider text-slate-400 uppercase">{title}</p>
        <p className="font-num text-xl lg:text-2xl font-bold text-slate-100 mt-2 truncate">
          {typeof numeric === "number" ? <CountUp value={numeric} format={numFormat} /> : value}
        </p>
        {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
      </div>
      <div className={`p-2.5 rounded-lg border ${accents[accent] || accents.slate}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  );
}

export function EmptyState({ title, description, action }) {
  return (
    <div
      data-testid="empty-state"
      className="bg-[#1E293B] border border-dashed border-slate-700 rounded-xl p-12 flex flex-col items-center text-center"
    >
      <div className="p-4 rounded-full bg-slate-800 mb-4">
        <Inbox className="w-8 h-8 text-slate-400" />
      </div>
      <h3 className="text-lg font-semibold text-slate-100">{title}</h3>
      <p className="text-sm text-slate-400 mt-1 max-w-md">{description}</p>
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function ConfirmDialog({ open, onOpenChange, title, description, onConfirm, loading }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-rose-500" /> {title}
          </AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="btn-confirm-cancel">Batal</AlertDialogCancel>
          <AlertDialogAction
            data-testid="btn-confirm-ok"
            className="bg-rose-600 hover:bg-rose-700 text-white"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ya, Lanjutkan"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function PaginationBar({ page, pages, total, onPage, limit, onLimit }) {
  if (total === 0) return null;
  return (
    <div
      data-testid="pagination-bar"
      className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-800/60"
    >
      <p className="text-xs text-slate-400">
        Menampilkan halaman <span className="font-semibold text-slate-200">{page}</span> dari{" "}
        {pages} — total <span className="font-semibold text-slate-200">{total}</span> data
      </p>
      <div className="flex items-center gap-2">
        {onLimit && (
          <select
            data-testid="select-page-size"
            className="rounded-lg border border-slate-700 bg-[#1E293B] px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-600"
            value={limit}
            onChange={(e) => onLimit(Number(e.target.value))}
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>{`${n} / halaman`}</option>
            ))}
          </select>
        )}
        <Button
          data-testid="btn-page-prev"
          variant="outline"
          size="sm"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          Sebelumnya
        </Button>
        <Button
          data-testid="btn-page-next"
          variant="outline"
          size="sm"
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
        >
          Berikutnya
        </Button>
      </div>
    </div>
  );
}

export const thClass =
  "bg-[#0F172A] text-slate-400 font-semibold uppercase text-xs tracking-wider p-3 border-b border-slate-800 whitespace-nowrap";
export const tdClass = "p-3 border-b border-slate-800/60 text-sm text-slate-200";
